import { enumNarrow } from '../helpers/enumGuard'
import { IOveradvance } from './bbc'
import { IAmendment, IClientInfo } from './client'
import { CommentaryKind } from './commentary'
import { ITermLoan } from './termLoan'
import { IUser } from './user'

/**
 * Analysis type discriminator. Amendment variants, the prospect OPS write-up
 * (committee deal summary), and the Loan Committee Report; Semi-Annual Review
 * will plug into the same parent GeneralAnalysis structure in a future phase.
 */
export enum GeneralAnalysisType {
  AmendmentABL = 'ABL Terms',
  AmendmentTermLoan = 'Term Loan',
  AmendmentOveradvance = 'Overadvance',
  AmendmentEquipmentLoan = 'Equipment Loan',
  OpsWriteup = 'OPS Write Up',
  Lcr = 'LCR',
}

export const AMENDMENT_ANALYSIS_TYPES: GeneralAnalysisType[] = [
  GeneralAnalysisType.AmendmentABL,
  GeneralAnalysisType.AmendmentTermLoan,
  GeneralAnalysisType.AmendmentOveradvance,
  GeneralAnalysisType.AmendmentEquipmentLoan,
]

/**
 * Statuses available to every analysis type.
 */
export enum AnalysisStatusBase {
  InProgress = 'In Progress',
  InReview = 'In Review',
  Approved = 'Approved',
  Finalized = 'Finalized',
  Archived = 'Archived',
}

/**
 * Amendment-specific status set. Extends the base with the legal-docs /
 * execution stages that only apply to loan amendments. There is no Finalized
 * step: the final details (effective date, restart term, summary of changes)
 * are confirmed on the way into Executed instead.
 */
export enum AmendmentAnalysisStatus {
  InProgress = 'In Progress',
  InReview = 'In Review',
  Approved = 'Approved',
  LegalDocs = 'Legal Docs',
  Executed = 'Executed',
  Archived = 'Archived',
}

export interface IOpenAmendmentAnalysis {
  analysisId: string
  status: AmendmentAnalysisStatus
  clientAmendmentId: string
  proposedChanges: ILoanAmendmentChange[]
}

export interface ILoanAmendmentChange {
  termName: string
  newValue: string | null
}

/**
 * OPS write-up status set (prospect committee deal summary). Superset of the
 * base ladder plus a leading `Not Started` (write-up created but untouched)
 * and a `Declined` branch (committee declined). Kept separate from the base
 * set so future non-amendment types aren't forced to carry these.
 */
export enum OpsWriteupStatus {
  NotStarted = 'Not Started',
  InProgress = 'In Progress',
  InReview = 'In Review',
  Approved = 'Approved',
  Finalized = 'Finalized',
  Declined = 'Declined',
  Archived = 'Archived',
}

/**
 * Union of every status value any analysis type may carry. The DB column is
 * `text` and each service validates the value against its type-specific set.
 */
export type AnyAnalysisStatus = AnalysisStatusBase | AmendmentAnalysisStatus | OpsWriteupStatus

export const ANALYSIS_STATUSES_BASE_ORDER: AnalysisStatusBase[] = [
  AnalysisStatusBase.InProgress,
  AnalysisStatusBase.InReview,
  AnalysisStatusBase.Approved,
  AnalysisStatusBase.Finalized,
  AnalysisStatusBase.Archived,
]

export const AMENDMENT_ANALYSIS_STATUSES_ORDER: AmendmentAnalysisStatus[] = [
  AmendmentAnalysisStatus.InProgress,
  AmendmentAnalysisStatus.InReview,
  AmendmentAnalysisStatus.Approved,
  AmendmentAnalysisStatus.LegalDocs,
  AmendmentAnalysisStatus.Executed,
  AmendmentAnalysisStatus.Archived,
]

/**
 * Amendment statuses a task board may set directly. Executed needs information
 * only the write-up collects (effective date, a fresh export, the term review),
 * so it stays in the write-up.
 */
export const BOARD_AMENDMENT_STATUSES: AmendmentAnalysisStatus[] =
  AMENDMENT_ANALYSIS_STATUSES_ORDER.filter((status) => status !== AmendmentAnalysisStatus.Executed)

/**
 * Display / membership order for OPS write-ups (drives the status dropdown and
 * the transition-membership checks). Includes the non-linear `Declined` state.
 */
export const OPS_WRITEUP_STATUSES_ORDER: OpsWriteupStatus[] = [
  OpsWriteupStatus.NotStarted,
  OpsWriteupStatus.InProgress,
  OpsWriteupStatus.InReview,
  OpsWriteupStatus.Approved,
  OpsWriteupStatus.Finalized,
  OpsWriteupStatus.Declined,
  OpsWriteupStatus.Archived,
]

/**
 * Linear "spine" used to derive OPS forward transitions. `Declined` is omitted
 * here because it's a branch off In Review, not a step in the ladder — it's
 * wired in via the `extraEdges` argument to buildAdjacencyMap below.
 */
const OPS_WRITEUP_LINEAR_ORDER: OpsWriteupStatus[] = [
  OpsWriteupStatus.NotStarted,
  OpsWriteupStatus.InProgress,
  OpsWriteupStatus.InReview,
  OpsWriteupStatus.Approved,
  OpsWriteupStatus.Finalized,
  OpsWriteupStatus.Archived,
]

export enum AmendmentAnalysisSectionKey {
  AmendmentOverview = 'AMENDMENT_OVERVIEW',
  DealDetail = 'DEAL_DETAIL',
  FinancialPerformance = 'FINANCIAL_PERFORMANCE',
  CollateralOverview = 'COLLATERAL_OVERVIEW',
  OwnershipValuation = 'OWNERSHIP_VALUATION',
  ItemsToMonitor = 'ITEMS_TO_MONITOR',
}

/** Cast-free narrower for AmendmentAnalysisSectionKey (see common/helpers/enumGuard). */
export const asAmendmentAnalysisSectionKey = enumNarrow(AmendmentAnalysisSectionKey).as

export interface IAnalysisSectionDefinition {
  key: AmendmentAnalysisSectionKey
  label: string
}

export const AMENDMENT_ANALYSIS_SECTIONS: IAnalysisSectionDefinition[] = [
  { key: AmendmentAnalysisSectionKey.AmendmentOverview, label: 'Overview' },
  { key: AmendmentAnalysisSectionKey.DealDetail, label: 'Deal Detail' },
  { key: AmendmentAnalysisSectionKey.FinancialPerformance, label: 'Financials' },
  { key: AmendmentAnalysisSectionKey.CollateralOverview, label: 'Collateral' },
  { key: AmendmentAnalysisSectionKey.OwnershipValuation, label: 'Ownership' },
  { key: AmendmentAnalysisSectionKey.ItemsToMonitor, label: 'Items to Monitor' },
]

/**
 * Section keys for the prospect OPS write-up (Deal Summary tab). Each value is
 * both the `general_analysis_sections.section_key` and the seeded
 * `commentary_section.tab` for that section.
 */
export enum OpsWriteupSectionKey {
  Overview = 'OVERVIEW',
  DealDynamics = 'DEAL_DYNAMICS',
  Financials = 'FINANCIALS',
  Collateral = 'COLLATERAL',
  QuestionsForCommittee = 'QUESTIONS_FOR_COMMITTEE',
}

/** Cast-free narrower for OpsWriteupSectionKey (see common/helpers/enumGuard). */
export const asOpsWriteupSectionKey = enumNarrow(OpsWriteupSectionKey).as

export interface IOpsWriteupSectionDefinition {
  /** Section key — also the `commentary_section.tab` value for this section. */
  key: OpsWriteupSectionKey
  /** Display label — the `commentary_section.name` value. */
  name: string
  /** 1-based display order. */
  order: number
  /**
   * Default commentary field labels seeded for this section, in display order.
   * Overview carries the six sub-sections of the Salesforce composite
   * `General_Comments__c` (see CANONICAL_SECTION_ORDER in the opsWriteup
   * workflow) so ticket 2a can map each canonical sub-section to its own box.
   */
  fields: string[]
}

export const OPS_WRITEUP_SECTIONS: IOpsWriteupSectionDefinition[] = [
  {
    key: OpsWriteupSectionKey.Overview,
    name: 'Company Overview',
    order: 1,
    fields: [
      'Company Description',
      'Revenue',
      'Fundraise / Cap Structure',
      'Cash Position',
      'Sales Channels / Distribution',
      'Future Growth / Growth Drivers',
    ],
  },
  {
    key: OpsWriteupSectionKey.DealDynamics,
    name: 'Deal Dynamics',
    order: 2,
    fields: ['Why / Motivation', 'Competition', 'Timing', 'Decision Makers'],
  },
  {
    key: OpsWriteupSectionKey.Financials,
    name: 'Financial Performance',
    order: 3,
    fields: ['Financial Comments'],
  },
  {
    key: OpsWriteupSectionKey.Collateral,
    name: 'Collateral',
    order: 4,
    fields: ['AR Comments', 'AP Comments', 'Inventory Comments'],
  },
  {
    key: OpsWriteupSectionKey.QuestionsForCommittee,
    name: 'Questions for Committee',
    order: 5,
    fields: ['Questions for Committee'],
  },
]

/**
 * Maps each single-value Salesforce OPS writeup field (from WRITEUP_FIELD_NAMES
 * in the opsWriteup workflow) to the commentary box it fills, by section + field
 * label. `General_Comments__c` is intentionally absent: it is a composite of six
 * sub-sections written into the Overview boxes and is handled specially by the
 * draft worker (see OPS_WRITEUP_OVERVIEW_SUBSECTION_MAP).
 */
export const OPS_WRITEUP_FIELD_MAP: Record<
  string,
  { sectionKey: OpsWriteupSectionKey; field: string }
> = {
  Latest_Why_Motivation__c: {
    sectionKey: OpsWriteupSectionKey.DealDynamics,
    field: 'Why / Motivation',
  },
  Latest_Competition__c: { sectionKey: OpsWriteupSectionKey.DealDynamics, field: 'Competition' },
  Latest_Timing__c: { sectionKey: OpsWriteupSectionKey.DealDynamics, field: 'Timing' },
  Latest_Decision_Makers__c: {
    sectionKey: OpsWriteupSectionKey.DealDynamics,
    field: 'Decision Makers',
  },
  Latest_Financial_Comments__c: {
    sectionKey: OpsWriteupSectionKey.Financials,
    field: 'Financial Comments',
  },
  Latest_AR_Comments__c: { sectionKey: OpsWriteupSectionKey.Collateral, field: 'AR Comments' },
  Latest_AP_Comments__c: { sectionKey: OpsWriteupSectionKey.Collateral, field: 'AP Comments' },
  Latest_Inventory_Comments__c: {
    sectionKey: OpsWriteupSectionKey.Collateral,
    field: 'Inventory Comments',
  },
  Questions_for_Committee__c: {
    sectionKey: OpsWriteupSectionKey.QuestionsForCommittee,
    field: 'Questions for Committee',
  },
}

/**
 * Maps each canonical `General_Comments__c` sub-section header (see
 * CANONICAL_SECTION_ORDER in the opsWriteup workflow's postprocess.ts, ALL CAPS)
 * to its Overview commentary box label. The draft worker splits the composite
 * General Comments text on these headers and fills one box per sub-section.
 */
export const OPS_WRITEUP_OVERVIEW_SUBSECTION_MAP: Record<string, string> = {
  'COMPANY DESCRIPTION': 'Company Description',
  REVENUE: 'Revenue',
  'FUNDRAISE / CAP STRUCTURE': 'Fundraise / Cap Structure',
  'CASH POSITION': 'Cash Position',
  'SALES CHANNELS / DISTRIBUTION': 'Sales Channels / Distribution',
  'FUTURE GROWTH / GROWTH DRIVERS': 'Future Growth / Growth Drivers',
}

/**
 * Section keys for the Loan Committee Report. Each value is both the
 * `general_analysis_tabs.tab_key` and the `commentary_section.tab` of that
 * section.
 *
 * These strings are NOT free to change: they are the `tab` values already
 * written by the LCR seed migration (1746135342299-default_lcr_commentary),
 * and they mirror `LCRTabsOrder` in common/constants/lcr.ts. Renaming one
 * orphans every commentary box under it.
 */
export enum LcrSectionKey {
  Overview = 'Overview',
  Financials = 'Financials',
  Forecast = 'Forecast',
  Transactions = 'Transactions',
  Receivables = 'Receivables',
  Payables = 'Payables',
  Inventory = 'Inventory',
  CapTable = 'CapTable',
  FieldExam = 'FieldExam',
}

/** Cast-free narrower for LcrSectionKey (see common/helpers/enumGuard). */
export const asLcrSectionKey = enumNarrow(LcrSectionKey).as

export interface ILcrSectionDefinition {
  /** Section key — also the `commentary_section.tab` value for this section. */
  key: LcrSectionKey
  /** Display label in the tab bar and the accordion header. */
  label: string
}

/**
 * The nine LCR sections, in the order the report renders them top to bottom.
 * Unlike OPS write-ups, the commentary fields under each section are NOT
 * code-defined — they come from the `kind='LCR'` commentary_section templates
 * seeded in the database and editable in UW Settings.
 */
export const LCR_ANALYSIS_SECTIONS: ILcrSectionDefinition[] = [
  { key: LcrSectionKey.Overview, label: 'Overview' },
  { key: LcrSectionKey.Financials, label: 'Financials' },
  { key: LcrSectionKey.Forecast, label: 'Forecast' },
  { key: LcrSectionKey.Transactions, label: 'Transactions' },
  { key: LcrSectionKey.Receivables, label: 'Receivables' },
  { key: LcrSectionKey.Payables, label: 'Payables' },
  { key: LcrSectionKey.Inventory, label: 'Inventory' },
  { key: LcrSectionKey.CapTable, label: 'Cap Table' },
  { key: LcrSectionKey.FieldExam, label: 'Field Exam' },
]

/**
 * Domain events emitted by every analysis type.
 */
export enum AnalysisEventTypeBase {
  StatusTransition = 'STATUS_TRANSITION',
  AssigneeChanged = 'ASSIGNEE_CHANGED',
  SectionMarkedNotApplicable = 'SECTION_MARKED_NOT_APPLICABLE',
  SectionReenabled = 'SECTION_REENABLED',
  ExhibitMarkedNotApplicable = 'EXHIBIT_MARKED_NOT_APPLICABLE',
  ExhibitReenabled = 'EXHIBIT_REENABLED',
  ExhibitSettingsChanged = 'EXHIBIT_SETTINGS_CHANGED',
}

/**
 * Amendment-specific domain events.
 */
export enum AmendmentAnalysisEventType {
  EffectiveDateSet = 'EFFECTIVE_DATE_SET',
  ExecutionRolledBack = 'EXECUTION_ROLLED_BACK',
  CommentaryAdded = 'COMMENTARY_ADDED',
  CommentaryUpdated = 'COMMENTARY_UPDATED',
  CommentaryDeleted = 'COMMENTARY_DELETED',
  TypeChanged = 'TYPE_CHANGED',
}

/**
 * OPS write-up (Deal Summary) specific domain events.
 */
export enum OpsWriteupEventType {
  /**
   * Arc drafted the write-up and its text landed in the commentary boxes.
   * Recorded because empty boxes otherwise look identical whether Arc never ran
   * or ran and failed to save, and because a re-draft has to know which boxes a
   * human has since rewritten.
   */
  ArcDrafted = 'ARC_DRAFTED',
  /**
   * The write-up was built from the deal's stored opportunity record rather
   * than written by anyone. Marks it as a historical record so the tab can say
   * so — an imported write-up must never read as a decision the committee made.
   */
  ImportedFromOpportunity = 'IMPORTED_FROM_OPPORTUNITY',
  /**
   * The owner was sent a batched digest of comments on this write-up. Doubles as
   * the watermark for the next batch, so a comment is neither counted twice nor
   * dropped between digests.
   */
  OwnerCommentsNotified = 'OWNER_COMMENTS_NOTIFIED',
}

export type AnyAnalysisEventType =
  AnalysisEventTypeBase | AmendmentAnalysisEventType | OpsWriteupEventType

export interface IStatusTransitionPayload {
  fromStatus: AnyAnalysisStatus
  toStatus: AnyAnalysisStatus
}

export interface IEffectiveDateSetPayload {
  effectiveDate: string
}

/** Emitted when an amendment leaves Executed; `effectiveDate` is the date that was cleared. */
export interface IExecutionRolledBackPayload {
  effectiveDate: string | null
}

export interface IAssigneeChangedPayload {
  fromAssigneeId: string | null
  toAssigneeId: string | null
}

export interface ISectionTogglePayload {
  sectionKey: string
}

/** Exhibit-level events (Not Applicable toggles and settings changes). */
export interface IExhibitEventPayload {
  exhibitKey: string
}

/**
 * Commentary add/update/delete events. `sectionKey` is the tab the commentary
 * lives under (so the log says where to look); `field` is the commentary's
 * field label. `commentaryId` lets the Update emitter coalesce a burst of
 * autosaves on the same commentary into a single log entry.
 */
export interface ICommentaryChangePayload {
  field: string
  sectionKey: string
  commentaryId?: string
}

export interface ITypeChangedPayload {
  fromType: GeneralAnalysisType
  toType: GeneralAnalysisType
}

export type GeneralAnalysisEventPayload =
  | IStatusTransitionPayload
  | IEffectiveDateSetPayload
  | IExecutionRolledBackPayload
  | IAssigneeChangedPayload
  | ISectionTogglePayload
  | IExhibitEventPayload
  | ICommentaryChangePayload
  | ITypeChangedPayload

export interface IGeneralAnalysisEvent {
  id: string
  analysisId: string
  eventType: AnyAnalysisEventType
  userId: string | null
  user?: IUser | null
  payload: GeneralAnalysisEventPayload
  createdAt: string
}

/**
 * One row of the Amendment Analysis "Board Members" exhibit, read from the
 * company's live CRM People rather than the underwriting intake snapshot.
 * `role` is the whole set the relationship carries (a director who is also an
 * investor shows as both); join it for display.
 */
export interface IAmendmentBoardMemberRow {
  // The CRM person behind the row, so the exhibit can link the name through to
  // their record — the whole point of sourcing this from live CRM People is that
  // there IS a record to open. Nullable because the row is only rendered when the
  // relationship has a person, but the column itself is nullable.
  personId: string | null
  fullName: string
  email: string | null
  // The board member's own outside affiliation, not the client company.
  company: string | null
  role: string[]
}

export interface IGeneralAnalysisSection {
  id: string
  analysisId: string
  sectionKey: string
  isNotApplicable: boolean
  createdAt: string
  updatedAt: string
}

/**
 * Per-analysis Not Applicable flag for an individual exhibit (chart/table)
 * inside a tab. Keyed by `exhibitKey` (matching the `<AmendmentExhibit id>`
 * passed by the section component). Type-agnostic so any general-analysis
 * type can reuse the same backend.
 */
export interface IGeneralAnalysisExhibit {
  id: string
  analysisId: string
  exhibitKey: string
  isNotApplicable: boolean
  settings: IAnalysisExhibitSettings
  createdAt: string
  updatedAt: string
}

/**
 * Reader-visible settings an exhibit keeps on its analysis. The row is already
 * keyed by `exhibitKey`, so the shape is flat per exhibit rather than
 * namespaced. Every field is optional: an absent one means "use the default the
 * exhibit resolves for itself".
 */
export interface IAnalysisExhibitSettings {
  /** Estimated Historical Line Turn — advance rates as fractions (0.85 = 85%). */
  arAdvanceRate?: number | null
  inventoryAdvanceRate?: number | null
  /** Customer Collections — wholesale share of net revenue as a fraction (0.8 = 80%). */
  wholesaleShare?: number | null
}

/**
 * Advance rates the Estimated Historical Line Turn chart falls back to when the
 * deal has none recorded. Underwriting's stated high-level estimate.
 */
export const DEFAULT_LINE_TURN_AR_ADVANCE_RATE = 0.85
export const DEFAULT_LINE_TURN_INVENTORY_ADVANCE_RATE = 0.65

/**
 * Exhibit key of the LCR Overview's line-turn chart. The key is the exhibit's
 * title, and the same string addresses it from three places (the React
 * wrapper, the Excel appender, the settings the chart reads), so it lives here
 * rather than as a literal in each.
 */
export const LINE_TURN_EXHIBIT_KEY = 'Estimated Historical Line Turn'

/**
 * Exhibit key of the LCR Transactions tab's Customer Collections exhibit. It keeps the key of the
 * Sales Channel Distribution exhibit it replaced, which its commentary and N/A flags are filed under.
 */
export const CUSTOMER_COLLECTIONS_EXHIBIT_KEY = 'Sales Channel Distribution'

/** Exhibit key of the LCR Receivables' wholesale dilution chart; its commentary box sits under it. */
export const WHOLESALE_DILUTION_EXHIBIT_KEY = 'Wholesale Dilution'

export interface IAmendmentAnalysisDetails {
  id: string
  analysisId: string
  effectiveDate: string | null
  /**
   * Subject FK — exactly one of clientAmendmentId / termLoanId /
   * overadvanceId is populated per row, matching the analysis `type`:
   *   AmendmentABL          → clientAmendmentId
   *   AmendmentTermLoan     → termLoanId
   *   AmendmentEquipmentLoan→ termLoanId
   *   AmendmentOveradvance  → overadvanceId
   */
  clientAmendmentId: string | null
  clientAmendment?: IAmendment | null
  termLoanId: string | null
  termLoan?: ITermLoan | null
  overadvanceId: string | null
  overadvance?: IOveradvance | null
  boxMonthFolderId: string | null
  boxMonthFolderCreated: boolean
  signedAmendmentFileId: string | null
  signedBoardConsentFileId: string | null
  writeupFileId: string | null
  legalWriteupFileId: string | null
  rawDataFileId: string | null
  createdAt: string
  updatedAt: string
}

/** Multipart field keys (`files[<key>]`) the signed-documents upload accepts. */
export enum AmendmentSignedDocumentKind {
  SignedAmendment = 'signedAmendment',
  BoardConsent = 'boardConsent',
}

/**
 * A row in the Operations → Amendments list. Unifies amendment *analyses*
 * (any type) with ABL `client_amendments` that have no analysis yet, so the
 * list surfaces every amendment. For analysis-less rows, `analysisId`,
 * `status` and `assignee` are null (rendered as "—" in the UI).
 */
export interface IAmendmentRow {
  // Stable React key: the analysis id, or `amendment:<id>` for a bare amendment.
  rowId: string
  analysisId: string | null
  clientAmendmentId: string | null
  termLoanId: string | null
  overadvanceId: string | null
  amendsExistingLoan: boolean
  clientName: string
  clientInfo?: IClientInfo
  type: GeneralAnalysisType
  status: AnyAnalysisStatus | null
  assigneeId: string | null
  assignee?: IUser | null
  startDate: string
  effectiveDate: string | null
  /**
   * Short human-readable summary of what the analysis changes (term names
   * for ABL; collateral / amount for TermLoan / Overadvance). Computed in
   * the List service from the linked amendment / term loan / overadvance.
   * Null for rows where no linked entity is loaded yet.
   */
  summaryOfChanges: string | null
  /**
   * Linked entities (with relations) loaded for the expandable row's
   * detail panel. Exactly one is set per row, matching the row's type:
   *   - ABL → clientAmendment (with clientAmendmentDetails)
   *   - TermLoan / Equipment → termLoan
   *   - Overadvance → overadvance
   * Bare ABL amendments (no analysis) carry `clientAmendment` only.
   */
  clientAmendment?: IAmendment | null
  termLoan?: ITermLoan | null
  overadvance?: IOveradvance | null
}

/**
 * The 3-state page view mode shared by every analysis surface: show the
 * Writeup (commentaries) only, the Exhibit (charts/tables) only, or Both.
 */
export type AnalysisViewMode = 'both' | 'writeup' | 'exhibit'

export const ANALYSIS_VIEW_MODES: AnalysisViewMode[] = ['both', 'writeup', 'exhibit']

/**
 * A single shared "saved view" for an analysis — every applied selection
 * across all exhibits, captured exactly as applied. One shared view per
 * analysis (not per-user, not multiple presets); last-write-wins.
 *
 * Selections are stored as RESOLVED ABSOLUTE values (e.g. an explicit
 * `{ startDate, endDate }` window, not "last 12 months"), because relative
 * ranges roll forward until the analysis is Executed. The saved view is
 * only applied while the analysis is pre-Executed; once Executed the
 * `artifactEndDate` anchoring system takes over and this row is retained
 * but no longer applied.
 */
export interface IAnalysisSavedView {
  /** Schema version so the blob shape can migrate later without a DB change. */
  version: number
  /** Writeup / Exhibit / Both page view mode. */
  viewMode?: AnalysisViewMode
  /** Whether the internal reply threads under commentaries are shown. */
  areRepliesShown?: boolean
  /**
   * Per-exhibit view-control selections, keyed by a stable control key
   * (e.g. `financials.reportingPeriod`). Values are the resolved absolute
   * selections the analyst applied — statement type, reporting period,
   * date range, growth toggle, group-by, chart metric, aggregation,
   * x-axis, the "last X months" cash-flow window, etc.
   */
  controls?: Record<string, unknown>
  /**
   * Per-table filter / quick-filter state captured verbatim from the URL
   * query string (the `${tableId}Filters` / `${tableId}QuickFilter` params
   * `useTable` writes). Restored by rewriting the URL on load.
   */
  urlParams?: Record<string, string>
}

/** Current schema version emitted when a client saves a view. */
export const ANALYSIS_SAVED_VIEW_VERSION = 1

export interface IGeneralAnalysis {
  id: string
  clientName: string
  /**
   * The deal this analysis belongs to. Set on OPS Write Up rows; null on
   * amendment analyses, which are client-level. The Deal Summary tab compares it
   * against the deal in view — a shared redux slice holds the PREVIOUS deal's
   * write-up until the next one lands, and without this it cannot tell them apart.
   */
  opsReportingId?: string | null
  clientInfo?: IClientInfo
  type: GeneralAnalysisType
  status: AnyAnalysisStatus
  assigneeId: string | null
  assignee?: IUser | null
  createdById: string
  createdBy?: IUser | null
  startDate: string
  amendmentDetails?: IAmendmentAnalysisDetails | null
  sections?: IGeneralAnalysisSection[]
  exhibits?: IGeneralAnalysisExhibit[]
  events?: IGeneralAnalysisEvent[]
  lastExportedAt: string | null
  /** When an amendment analysis last reached Executed; cleared when it is rolled back. */
  executedAt: string | null
  contentUpdatedAt: string
  /**
   * The single shared saved view for this analysis, or null when none has
   * been saved (each exhibit then loads its defaults — "Default view").
   */
  savedView: IAnalysisSavedView | null
  /** When the saved view was last written (for "last saved by X on <date>"). */
  savedViewUpdatedAt: string | null
  savedViewUpdatedById: string | null
  savedViewUpdatedBy?: IUser | null
  createdAt: string
  updatedAt: string
}

/**
 * An analysis is read-only for saved-view purposes at its terminal states — the
 * saved view is retained but no longer applied, and the Save/Reset controls are
 * hidden. Type-aware because the terminal states differ by surface:
 *  - Amendment: Executed or Archived (Executed hands off to `artifactEndDate`
 *    anchoring).
 *  - OPS write-up (Deal Summary) and LCR: Finalized or Archived. Both stop
 *    accepting content edits earlier than this (see `isAnalysisEditLocked`) —
 *    an Approved report still lets a reviewer re-file the exhibits while the
 *    committee reads it, and only freezes the shared view once it is final.
 */
export const isAnalysisViewLocked = (
  type?: GeneralAnalysisType | null,
  status?: AnyAnalysisStatus | string | null,
): boolean => {
  if (type && (isOpsWriteupType(type) || isLcrType(type))) {
    return status === AnalysisStatusBase.Finalized || status === AnalysisStatusBase.Archived
  }
  return status === AmendmentAnalysisStatus.Executed || status === AmendmentAnalysisStatus.Archived
}

/**
 * Statuses at which an analysis stops accepting content edits — commentary
 * add/update/delete, and the Not Applicable toggles. Distinct from
 * `isAnalysisViewLocked`, which only freezes the saved view (filters + date
 * ranges); a surface can be view-locked later than it is edit-locked.
 *
 * Deliberately per-type, because "done" means something different on each:
 *  - Amendment: Executed (terms committed) or Archived.
 *  - OPS write-up / LCR: the committee has decided (Approved onward), or the
 *    record is Archived. Declined is an OPS-only branch off In Review.
 *
 * The status enums share their string values, so membership is checked on the
 * per-type list rather than on the raw status alone.
 */
const EDIT_LOCKED_STATUSES: Record<'amendment' | 'opsWriteup' | 'lcr', string[]> = {
  amendment: [AmendmentAnalysisStatus.Executed, AmendmentAnalysisStatus.Archived],
  opsWriteup: [
    OpsWriteupStatus.Approved,
    OpsWriteupStatus.Finalized,
    OpsWriteupStatus.Declined,
    OpsWriteupStatus.Archived,
  ],
  lcr: [AnalysisStatusBase.Approved, AnalysisStatusBase.Finalized, AnalysisStatusBase.Archived],
}

/**
 * Whether the analysis has reached a status that freezes its content. Callers
 * layer their own surface rules on top — the LCR, for instance, is
 * additionally editable only while the client is in due diligence.
 */
export const isAnalysisEditLocked = (
  type?: GeneralAnalysisType | null,
  status?: AnyAnalysisStatus | string | null,
): boolean => {
  if (!type || !status) return false
  if (isOpsWriteupType(type)) return EDIT_LOCKED_STATUSES.opsWriteup.includes(status)
  if (isLcrType(type)) return EDIT_LOCKED_STATUSES.lcr.includes(status)
  return EDIT_LOCKED_STATUSES.amendment.includes(status)
}

export const isAmendmentAnalysisType = (type: GeneralAnalysisType): boolean =>
  AMENDMENT_ANALYSIS_TYPES.includes(type)

/**
 * A Deal Summary is read-only once the committee has decided (Approved /
 * Declined), the deal is Finalized, or it has been Archived. Shared so the tab,
 * the write guards, and Arc's lookup all report the same lock.
 */
export const LOCKED_OPS_WRITEUP_STATUSES: string[] = [
  OpsWriteupStatus.Approved,
  OpsWriteupStatus.Finalized,
  OpsWriteupStatus.Declined,
  OpsWriteupStatus.Archived,
]

export const isOpsWriteupLocked = (status?: AnyAnalysisStatus | string | null): boolean =>
  LOCKED_OPS_WRITEUP_STATUSES.includes(status ?? '')

export const isOpsWriteupType = (type: GeneralAnalysisType): boolean =>
  type === GeneralAnalysisType.OpsWriteup

export const isLcrType = (type: GeneralAnalysisType): boolean => type === GeneralAnalysisType.Lcr

/**
 * Maps an analysis type to the CommentaryKind its sections/commentary live
 * under. Amendment types → AMENDMENT_ANALYSIS; OPS write-ups → OPS_WRITEUP;
 * the Loan Committee Report → LCR. Used by the commentary CRUD services to
 * resolve the right section template.
 */
export const commentaryKindForAnalysisType = (type: GeneralAnalysisType): CommentaryKind => {
  if (isOpsWriteupType(type)) return CommentaryKind.OpsWriteup
  if (isLcrType(type)) return CommentaryKind.LCR
  return CommentaryKind.AmendmentAnalysis
}

/**
 * An amendment analysis is "complete" once its legal docs are executed (or the
 * analysis is later archived). At that point the amendment's Box folder holds
 * the executed artifacts, so the UI can deep-link straight to the folder rather
 * than the individual legal document.
 */
export const isAmendmentAnalysisComplete = (
  status?: AmendmentAnalysisStatus | string | null,
): boolean =>
  status === AmendmentAnalysisStatus.Executed || status === AmendmentAnalysisStatus.Archived

/**
 * Returns the ordered list of statuses valid for a given analysis type.
 * Used by the frontend (dropdown filtering) and backend (transition validation).
 * The LCR carries no extra states beyond the base ladder, so it falls through
 * to ANALYSIS_STATUSES_BASE_ORDER.
 */
export const getStatusesForType = (type: GeneralAnalysisType): AnyAnalysisStatus[] => {
  if (isAmendmentAnalysisType(type)) {
    return [...AMENDMENT_ANALYSIS_STATUSES_ORDER]
  }
  if (isOpsWriteupType(type)) {
    return [...OPS_WRITEUP_STATUSES_ORDER]
  }
  return [...ANALYSIS_STATUSES_BASE_ORDER]
}

/**
 * Adjacency rules for OPS write-up and LCR status transitions. Without these
 * the membership check on `getStatusesForType()` lets reviewers be bypassed
 * and lets Approved↔InProgress re-fire the export pipeline arbitrarily.
 * Amendments are exempt and move freely (see `getAllowedNextStatuses`).
 *
 * Rule shape per status: forward step (the next entry in the ordered
 * array) + explicit back-transitions + universal `→ Archived` edge.
 */
const buildAdjacencyMap = <T extends AnyAnalysisStatus>(
  order: readonly T[],
  archived: T,
  back: Partial<Record<T, T>>,
  extraEdges: Partial<Record<T, T[]>> = {},
): Record<string, T[]> => {
  const map: Record<string, T[]> = {}
  for (let i = 0; i < order.length; i += 1) {
    const from = order[i]
    if (from === archived) continue
    const next: T[] = []
    const forward = order[i + 1]
    if (forward && forward !== archived) next.push(forward)
    const backStep = back[from]
    if (backStep) next.push(backStep)
    next.push(archived)
    map[from] = next
  }
  // Archived must remain reachable for un-archive back to the first status.
  map[archived] = [order[0]]
  // Merge non-linear branches the linear spine can't express (e.g. OPS
  // "Declined" off In Review, plus Declined's own reopen/archive edges).
  for (const [from, targets] of Object.entries(extraEdges) as [T, T[] | undefined][]) {
    if (!targets) continue
    const existing = map[from] ?? []
    map[from] = [...existing, ...targets.filter((target) => !existing.includes(target))]
  }
  return map
}

const BASE_TRANSITIONS = buildAdjacencyMap<AnalysisStatusBase>(
  ANALYSIS_STATUSES_BASE_ORDER,
  AnalysisStatusBase.Archived,
  {
    [AnalysisStatusBase.InReview]: AnalysisStatusBase.InProgress,
    [AnalysisStatusBase.Approved]: AnalysisStatusBase.InReview,
    [AnalysisStatusBase.Finalized]: AnalysisStatusBase.Approved,
  },
)

const OPS_WRITEUP_TRANSITIONS = buildAdjacencyMap<OpsWriteupStatus>(
  OPS_WRITEUP_LINEAR_ORDER,
  OpsWriteupStatus.Archived,
  {
    [OpsWriteupStatus.InReview]: OpsWriteupStatus.InProgress,
    [OpsWriteupStatus.Approved]: OpsWriteupStatus.InReview,
    [OpsWriteupStatus.Finalized]: OpsWriteupStatus.Approved,
  },
  {
    // Committee declines from In Review; a declined write-up can be reopened
    // to In Progress or archived.
    [OpsWriteupStatus.InReview]: [OpsWriteupStatus.Declined],
    [OpsWriteupStatus.Declined]: [OpsWriteupStatus.InProgress, OpsWriteupStatus.Archived],
  },
)

/**
 * Returns the statuses a row in `fromStatus` is allowed to transition to,
 * given the analysis type. Amendments may move to any other status in their
 * ladder, in either direction — leaving Executed unwinds the executed terms
 * (see generalAnalysis/UpdateStatus). OPS write-ups and the LCR keep the
 * adjacency rules; an empty array means terminal (no exit).
 */
export const getAllowedNextStatuses = (
  type: GeneralAnalysisType,
  fromStatus: AnyAnalysisStatus,
): AnyAnalysisStatus[] => {
  if (isAmendmentAnalysisType(type)) {
    return AMENDMENT_ANALYSIS_STATUSES_ORDER.filter((status) => status !== fromStatus)
  }
  const map = isOpsWriteupType(type) ? OPS_WRITEUP_TRANSITIONS : BASE_TRANSITIONS
  return map[fromStatus] ?? []
}

/** Cast-free narrower for GeneralAnalysisType (see common/helpers/enumGuard). */
export const asGeneralAnalysisType = enumNarrow(GeneralAnalysisType).as
